import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { Button, Card, Modal, Text } from "../../../../components";
import { useApi, usePayment } from "../../../../hooks";
import { Constants, Enums } from "../../../../utils";
import Localization from "../../../../localization";
import { useNavigate } from "react-router-dom";
import ManagePayment from "./managePayment";
import useViewModel from "../../viewModel";
import ModifyCard from "./modifyCard";
import React, { useRef } from "react";
import { format } from "date-fns";

const Payment = () => {
  const managePaymentModalRef = useRef();
  const confirmationModalRef = useRef();
  const navigate = useNavigate();
  const addCardModalRef = useRef();
  const payment = usePayment();
  const { execute } = useApi();
  const {
    uuid,
    name,
    upgrade,
    paymentInfo,
    refresh = () => {},
    deleteSubscription = () => {},
    reactivateSubscription = () => {},
  } = useViewModel();

  const planName = paymentInfo?.name ?? Localization.general.na;
  const isMonthly = paymentInfo?.paymentPlan?.name === "monthly";
  const isCanceled = paymentInfo.state === "cancelled";
  const type = paymentInfo?.paymentMethod?.type;
  const isCardMissing = type === Enums.paymentType.activation;
  const isInvoice = type === Enums.paymentType.invoice;
  const priceWithCurrency = `${
    paymentInfo?.paymentPlan?.amount ?? 0
  } kr/${(isMonthly
    ? Localization.general.month
    : Localization.general.year
  ).toLowerCase()}`;
  const isActive = !(
    isCanceled &&
    !!paymentInfo?.nextPayment &&
    new Date(paymentInfo.nextPayment) <= new Date()
  );
  const managePaymentOptions = [
    {
      onClick: () => addCardModalRef.current.open(),
      title: isCardMissing
        ? Localization.service.payment_btn_0_missing_card
        : Localization.service.payment_btn_0,
    },
    upgrade && {
      title: Localization.service.payment_btn_3,
      onClick: () =>
        navigate(
          `/${Enums.pagesNames.servicePaymentPlans}?params=${JSON.stringify({
            uuid,
          })}`
        ),
    },
    {
      title: Localization.service.payment_btn_1,
      onClick: () => confirmationModalRef.current.open({ immediately: true }),
    },
    {
      title: Localization.service.payment_btn_2,
      onClick: () => confirmationModalRef.current.open(),
    },
  ].filter(Boolean);

  const updateCard = async (card) => {
    await execute(() =>
      payment.cardSetup({
        card,
        serviceUuid: uuid,
        serviceName: name,
        successDelay: 7000,
        servicePaymentPlan: paymentInfo?.paymentPlan?.name,
      })
    );
    await refresh(false);
  };

  const reactivate = async () => {
    if (isCardMissing)
      return addCardModalRef.current.open({ onDone: reactivate });
    await reactivateSubscription({ activated: isActive });
  };

  const cancelService = async (params) => {
    await deleteSubscription(params);
    confirmationModalRef.current.close();
    managePaymentModalRef.current.close();
  };

  return (
    <Card
      title={Localization.service.card_5_title}
      extra={
        <Text level={4} type={isActive ? "secondary" : "disabled"}>
          {isActive ? <CheckCircleFilled /> : <CloseCircleFilled />}
          {` ${
            isActive
              ? Localization.general.active
              : Localization.general.inactive
          }`}
        </Text>
      }
    >
      <div className="m-25-b">
        <Text
          level={2}
          weight={700}
          lineHeight="3rem"
          type="secondary"
          children={planName}
        />
        <Text
          level={4}
          weight={700}
          className="m-3-t"
          lineHeight="normal"
          children={priceWithCurrency}
        />
      </div>
      {isInvoice ? (
        <React.Fragment>
          <Text
            level={5}
            weight={700}
            type="secondary"
            children={`${Localization.general.payment_method}: ${Localization.general.invoice}`}
          />
          <Text
            type="secondary"
            className="m-3-t"
            children={Localization.formatString(
              Localization.service.payment_invoice_desc,
              <Text
                level="link"
                underline
                weight={600}
                children={Constants.Project.emails.support}
              />
            )}
          />
        </React.Fragment>
      ) : isCanceled ? (
        <React.Fragment>
          <Text
            level={5}
            weight={700}
            children={Localization.service.payment_desc_cancelled_title}
          />
          {isActive && (
            <Text
              weight={500}
              className="m-3-t"
              type={"secondary"}
              children={`${
                Localization.service.payment_desc_cancelled_subtitle
              } ${format(
                paymentInfo?.nextPayment ?? new Date(),
                "yyyy-MM-dd"
              )}`}
            />
          )}
          <Button
            type="primary"
            className="m-30-t"
            onClick={reactivate}
            title={Localization.general.reactivate}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Text
            level={5}
            weight={700}
            type="secondary"
            children={`${Localization.service.payment_cancelled_title} ${format(
              paymentInfo?.nextPayment ?? new Date(),
              "yyyy-MM-dd"
            )}`}
          />
          {isActive && (
            <Text
              weight={500}
              className="m-3-t"
              type={isCardMissing ? "primary" : "secondary"}
              children={
                isCardMissing
                  ? Localization.service.payment_cancelled_missing_card_subtitle
                  : `${Localization.general.payment_method}: ${paymentInfo?.paymentMethod?.name}`
              }
            />
          )}
          <Button
            type="primary"
            className="m-30-t"
            title={Localization.general.manage}
            onClick={() => managePaymentModalRef.current.open()}
          />
        </React.Fragment>
      )}
      <Modal
        ref={addCardModalRef}
        title={
          isCardMissing
            ? Localization.service.payment_modal_add_card_missing_card_title
            : Localization.service.payment_btn_3
        }
        child={({ onDone = () => {} } = {}) => (
          <ModifyCard
            onDone={async (card) => {
              await updateCard(card);
              await onDone();
              addCardModalRef.current.close();
              managePaymentModalRef.current.close();
            }}
          />
        )}
      />
      <Modal
        ref={confirmationModalRef}
        title={Localization.service.payment_modal_cancelled_title}
        child={(params) => (
          <Modal.Dialog
            description={Localization.service.payment_modal_cancelled_subtitle}
            buttons={[
              {
                type: "primary",
                onClick: () => cancelService(params),
                title: Localization.general.yes,
              },
              {
                title: Localization.general.no,
                onClick: () => confirmationModalRef.current.close(),
              },
            ]}
          />
        )}
      />
      <Modal
        ref={managePaymentModalRef}
        title={Localization.service.payment_modal_manage_title}
      >
        <ManagePayment
          options={managePaymentOptions}
          onCancel={() => managePaymentModalRef.current.close()}
          onSelect={(index) => managePaymentOptions[index].onClick()}
        />
      </Modal>
    </Card>
  );
};

export default Payment;
